import { getLogger } from '@jitsi/logger';

import { JitsiConferenceEvents } from '../../react/features/base/lib-jitsi-meet';
import { MEDIA_TYPE } from '../../react/features/base/media/constants';
import { getLocalAudioTrackData } from '../components/analytics/functions';
import { sendStatsToKibana } from '../components/analytics/kibana/actions';
import { sendStatsToKibanaNoDispatch } from '../components/analytics/kibana/actions.any';
import { KIBANA_MESSAGES } from '../components/analytics/kibana/constants';
import WildixEvents from '../service/WildixEvents';

const logger = getLogger('AudioOutputProblemDetector');
const MAX_TURN_RESTART_ATTEMPTS = 2;
const ELEMENTS_THRESHOLD_INITIAL_OR_FINAL = 10;
const ELEMENTS_THRESHOLD_AFTER_RESTART = 5;

/**
 * Collects the average audio upload per participant from the local stats
 * participant and compares them to detect potential audio problem for a participant.
 */
export default class AudioOutputProblemDetector {
    /**
     * Creates new <tt>AudioOutputProblemDetector</tt> instance.
     *
     * @param {JitsiConference} conference - The conference instance to be monitored.
     */
    constructor(conference) {
        this._conference = conference;
        this.isReconnectedWithTurn = false;
        this.isNotificationShowed = false;
        this.turnRestartCount = 0;
        this.jitsiTrasportArray = [];
        this.avgAudioUpload = [];
        this._localAudioTrack = null;
        this._isDisposed = false;

        this.intervalStats = null;
        this.timeUpdateStats = 2000;

        this._onLocalAudioLevelsReport = this._onLocalAudioLevelsReport.bind(this);
        this._jitsiStatsCollector = this._jitsiStatsCollector.bind(this);
        this._clearUserData = this._clearUserData.bind(this);
        this._findAverUpload = this._findAverUpload.bind(this);
        this._prepareDataToSend = this._prepareDataToSend.bind(this);
        this._reconnectWithTurn = this._reconnectWithTurn.bind(this);
        this._resetFlags = this._resetFlags.bind(this);
        this._onTrackAdded = this._onTrackAdded.bind(this);
        this._onTrackMuteChanged = this._onTrackMuteChanged.bind(this);

        this._conference.addEventListener(JitsiConferenceEvents.CONFERENCE_LEFT, this.dispose.bind(this));
        this._conference.addEventListener(JitsiConferenceEvents.TRACK_ADDED, this._onTrackAdded);
        this._conference.addEventListener(JitsiConferenceEvents.TRACK_MUTE_CHANGED, this._onTrackMuteChanged);
    }

    /**
     * Handles track added event by setting up stats collection and analytics.
     *
     * @returns {void}
     */
    _onTrackAdded(audioTrack) {
        if (this._isDisposed
            || !audioTrack.isLocal()
            || audioTrack.getType() !== MEDIA_TYPE.AUDIO) {
            return;
        }

        this._localAudioTrack = audioTrack;

        if (!this._localAudioTrack.isMuted()) {
            this._addStatsListenerAndStartInterval();
        }
    }

    /**
     * Handles track mute state changes.
     *
     * @param {JitsiTrack} track - The track that was muted/unmuted.
     * @returns {void}
     */
    _onTrackMuteChanged(track) {
        if (this._isDisposed
            || !track.isLocal()
            || track.getType() !== MEDIA_TYPE.AUDIO) {
            return;
        }

        if (track.isMuted()) {
            this._removeStatsListenerAndInterval();
        } else {
            this._addStatsListenerAndStartInterval();
        }
    }

    /**
     * Adds stats listener and starts interval for analytics collection.
     *
     * @returns {void}
     */
    _addStatsListenerAndStartInterval() {
        if (this._isDisposed) {
            return;
        }

        try {
            // Remove any existing listeners and intervals first
            this._removeStatsListenerAndInterval();

            this._conference.statistics.addConnectionStatsListener(this._jitsiStatsCollector);
            this._sendAnalyticFromPeerConnection();
        } catch (e) {
            logger.error('Error adding stats listener and starting interval:', e);
        }
    }

    /**
     * Removes stats listener and clears the interval for analytics collection.
     *
     * @returns {void}
     */
    _removeStatsListenerAndInterval() {
        try {
            this._conference.statistics.removeConnectionStatsListener(this._jitsiStatsCollector);
            if (this.intervalStats) {
                clearInterval(this.intervalStats);
                this.intervalStats = null;
            }
        } catch (e) {
            logger.error('Error removing stats listener and interval:', e);
        }
    }

    /**
     * Resets all flags to their initial state.
     *
     * @returns {void}
     */
    _resetFlags() {
        this.isReconnectedWithTurn = false;
        this.isNotificationShowed = false;
        this.turnRestartCount = 0;
    }

    /**
     * Collects transport statistics from the peer connection.
     *
     * @param {Object} tpc - The peer connection instance.
     * @param {Object} stats - The statistics object containing transport data.
     * @returns {void}
     */
    _jitsiStatsCollector(tpc, { transport }) {
        if (transport && transport.length > 0) {
            this.jitsiTrasportArray = transport;
        }
    }

    /**
     * Sets up periodic collection of peer connection statistics.
     *
     * @returns {void}
     */
    _sendAnalyticFromPeerConnection() {
        let dtlsStatus = null;

        this.intervalStats = setInterval(() => {
            try {
                this._conference?.jvbJingleSession?.peerconnection?.getStats(null).then(stats => {
                    stats.forEach(report => {
                        if (report.type === 'transport') {
                            dtlsStatus = report.dtlsState;
                        }
                        if (report.type === 'outbound-rtp' && report.kind === 'audio') {
                            this._onLocalAudioLevelsReport(report.bytesSent, dtlsStatus);
                        }
                    });
                })
                .catch(error => {
                    logger.error('Error getting peer connection stats:', error);
                });
            } catch (e) {
                logger.error('Error accessing peer connection for stats:', e);
            }
        }, this.timeUpdateStats);
    }

    /**
     * Calculates the average audio upload value.
     *
     * @returns {number} The average audio upload value.
     */
    _findAverUpload() {
        return (this.avgAudioUpload.reduce((sum, currentValue) => sum + currentValue, 0) / this.avgAudioUpload.length)
            || 0;
    }

    /**
     * Prepares data for sending to analytics.
     *
     * @param {Array} array - The array of data to prepare.
     * @returns {Object} The prepared data object.
     */
    _prepareDataToSend(array) {
        const object = {};

        array.forEach((item, index) => {
            object[`item${index}`] = item;
        });

        return object;
    }

    /**
     * Reconnecting with disabled UDP candidates.
     *
     * @returns {void}
     */
    _reconnectWithTurn() {
        const transport = this.jitsiTrasportArray;

        if (this.turnRestartCount >= MAX_TURN_RESTART_ATTEMPTS) {
            return;
        }

        this.isReconnectedWithTurn = true;
        this.turnRestartCount++;
        try {
            this._conference.options.config.forceTurnRelay = true;
            this._conference._restartMediaSessions();

            if (typeof APP !== 'undefined') {
                APP.store.dispatch(sendStatsToKibana([ {
                    transport,
                    restartTurnCount: this.turnRestartCount
                } ], KIBANA_MESSAGES.AudioProblemMediaRestarted));
            } else {
                sendStatsToKibanaNoDispatch(this._conference, [ {
                    transport,
                    restartTurnCount: this.turnRestartCount
                } ], KIBANA_MESSAGES.AudioProblemMediaRestarted);
            }
        } catch (e) {
            logger.error('Media session restarted with TURN: not started', e);
        }
    }

    /**
     * A listener for audio level data retrieved by the local stats.
     *
     * @param {number} bytesSent - The number of bytes sent.
     * @param {string} dtlsState - The DTLS state.
     * @returns {void}
     */
    _onLocalAudioLevelsReport(bytesSent, dtlsState) {
        const tpc = this._conference.getActivePeerConnection();

        if (!tpc) {
            return;
        }

        this.avgAudioUpload.push(bytesSent);

        const avgAudioUpload = this._findAverUpload();
        const requiredElements = this.turnRestartCount >= MAX_TURN_RESTART_ATTEMPTS
            ? ELEMENTS_THRESHOLD_INITIAL_OR_FINAL
            : this.isReconnectedWithTurn ? ELEMENTS_THRESHOLD_AFTER_RESTART : ELEMENTS_THRESHOLD_INITIAL_OR_FINAL;

        const { connectionState } = tpc || {};
        const isTransportEstablished = connectionState === 'connected';

        if ((avgAudioUpload > 0 && this.avgAudioUpload.length >= requiredElements) || isTransportEstablished) {
            this.dispose();

            return;
        }

        if (this.avgAudioUpload.length >= requiredElements && avgAudioUpload <= 0 && !this.isReconnectedWithTurn) {
            logger.warn(`Wildix: Detected problem with the local audio upload: ${this.avgAudioUpload}`);
            try {
                const { iceConnectionState, signalingState } = tpc || {};

                const objectToSend = {
                    connectionState,
                    dtlsState,
                    transport: this.jitsiTrasportArray,
                    iceConnectionState,
                    signalingState,
                    audioUploadObject: this._prepareDataToSend(this.avgAudioUpload),
                    avgAudioUpload,
                    audioTrack: getLocalAudioTrackData(this._localAudioTrack)
                };

                if (navigator.product !== 'ReactNative') {
                    APP.store.dispatch(sendStatsToKibana([ {
                        ...objectToSend
                    } ], KIBANA_MESSAGES.AudioProblemNoAudio));
                } else {
                    sendStatsToKibanaNoDispatch(this._conference, [ {
                        ...objectToSend
                    } ], KIBANA_MESSAGES.AudioProblemNoAudio);
                }
            } catch (e) {
                logger.error(e);
            }
            this._clearUserData(false);
            this._reconnectWithTurn();

            return;
        }

        // Handle TURN restart attempts
        if (this.avgAudioUpload.length >= requiredElements && avgAudioUpload <= 0) {
            if (this.turnRestartCount >= MAX_TURN_RESTART_ATTEMPTS) {
                if (!this.isNotificationShowed) {
                    this.isNotificationShowed = true;
                    this._conference.eventEmitter.emit(WildixEvents.SHOW_NOTIFICATION_NO_AUDIO);
                    this.dispose();
                }

                return;
            }

            if (this.turnRestartCount < MAX_TURN_RESTART_ATTEMPTS) {
                this._reconnectWithTurn();
                this._clearUserData(false);
            }
        }
    }

    /**
     * Clears the data stored for a participant.
     *
     * @param {boolean} resetAll - Whether to reset all flags.
     * @returns {void}
     */
    _clearUserData(resetAll = false) {
        this.avgAudioUpload = [];
        if (resetAll) {
            this._resetFlags();
        }
    }

    /**
     * Disposes the allocated resources.
     *
     * @returns {void}
     */
    dispose() {
        // If conference is already undefined, we've already disposed
        if (!this._conference || this._isDisposed) {
            return;
        }

        this._isDisposed = true;

        try {
            this._removeStatsListenerAndInterval();
            this._conference.removeEventListener(JitsiConferenceEvents.TRACK_ADDED, this._onTrackAdded);
            this._conference.removeEventListener(JitsiConferenceEvents.TRACK_MUTE_CHANGED, this._onTrackMuteChanged);
        } catch (e) {
            logger.error('Error removing listeners:', e);
        }

        this._clearUserData(true);
        this._conference = undefined;
        this._localAudioTrack = null;
        this.avgAudioUpload = undefined;
    }
}
